import { useState, useEffect, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchWithAuth } from "@/lib/api";
import { API_BASE } from "@/config/api";
import { getExchangeLabel } from "@/lib/exchange-mapping";
import { getCurrencySymbol, getCurrencyName, formatPrice } from "@/lib/formatters";
import type { Asset, AssetChart, StockAsset, EtfAsset, FundAsset } from "@/schemas";
import { TrendingUp, TrendingDown, X } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { AssetTypeBadge } from "./asset-utils";
import { AssetLogo } from "./asset-logo";
import {
  computeAssetPriceChange,
  filterChartToLatestIntradaySession,
  getValidatedPreviousClose,
} from "./price-change";

const TIME_PERIODS = [
  { value: "1d", label: "1D" },
  { value: "1w", label: "1W" },
  { value: "1m", label: "1M" },
  { value: "1y", label: "1Y" },
  { value: "max", label: "Max" },
];

const chartConfig = {
  price: {
    label: "Price",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

function getEvenlySpacedTicks<T>(items: T[], count: number): T[] {
  if (items.length <= count) {
    return items;
  }

  return Array.from({ length: count }, (_, index) => {
    const itemIndex = Math.round((index * (items.length - 1)) / (count - 1));
    return items[itemIndex];
  });
}

function getNiceStep(rawStep: number): number {
  if (rawStep <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
}

function getPriceTicks(min: number, max: number, count: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [];
  }

  if (min === max) {
    return [min];
  }

  const step = getNiceStep((max - min) / (count - 1));
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];

  for (let tick = start; tick <= end + step / 2; tick += step) {
    ticks.push(Number(tick.toPrecision(12)));
  }

  return ticks;
}

interface AssetChartViewProps {
  asset: Asset;
  onClose?: () => void;
  otherExchanges?: Asset[]; // List of same asset on other exchanges
  onExchangeChange?: (assetId: string) => void;
  className?: string;
  compact?: boolean; // Smaller chart for side-by-side layout
  showFiveYearPeriod?: boolean;
}

export function AssetChartView({ 
  asset, 
  onClose, 
  otherExchanges = [], 
  onExchangeChange,
  className,
  compact = false,
  showFiveYearPeriod = false
}: AssetChartViewProps) {
  const [period, setPeriod] = useState("1m");
  const [chartData, setChartData] = useState<AssetChart | null>(null);
  const [dailyReferenceChartData, setDailyReferenceChartData] = useState<AssetChart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timePeriods = useMemo(
    () =>
      showFiveYearPeriod
        ? [
            ...TIME_PERIODS.slice(0, -1),
            { value: "5y", label: "5Y" },
            TIME_PERIODS[TIME_PERIODS.length - 1],
          ]
        : TIME_PERIODS,
    [showFiveYearPeriod]
  );

  useEffect(() => {
    const fetchChart = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchWithAuth(
          `${API_BASE}/charts/${asset.asset_id}?period=${period === "5y" ? "max" : period}`,
          { method: "GET" }
        );
        if (response.ok) {
          const data = await response.json();
          setChartData(data);
        } else {
          setError("No chart data available");
          setChartData(null);
        }
      } catch (err) {
        console.error("Error fetching chart:", err);
        setError("Failed to load chart");
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchChart();
  }, [asset.asset_id, period]);

  useEffect(() => {
    if (period !== "1d") {
      return;
    }

    const fetchDailyReferenceChart = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_BASE}/charts/${asset.asset_id}?period=1y`,
          { method: "GET" }
        );
        if (response.ok) {
          const data = await response.json();
          setDailyReferenceChartData(data);
        } else {
          setDailyReferenceChartData(null);
        }
      } catch (err) {
        console.error("Error fetching daily reference chart:", err);
        setDailyReferenceChartData(null);
      }
    };

    fetchDailyReferenceChart();
  }, [asset.asset_id, period]);

  const displayedChartData = useMemo(() => {
    if (period === "1d") {
      return filterChartToLatestIntradaySession(chartData);
    }

    if (period !== "5y" || !chartData?.price_points?.length) {
      return chartData;
    }

    const lastTimestamp = chartData.price_points[chartData.price_points.length - 1].timestamp;
    const cutoff = new Date(lastTimestamp);
    cutoff.setFullYear(cutoff.getFullYear() - 5);

    return {
      ...chartData,
      price_points: chartData.price_points.filter(
        (point) => new Date(point.timestamp) >= cutoff
      ),
    };
  }, [chartData, period]);

  // Get current price from chart or asset
  const currentPrice = displayedChartData?.price_points?.length
    ? displayedChartData.price_points[displayedChartData.price_points.length - 1].price
    : (asset.type === "stock" || asset.type === "etf" || asset.type === "fund")
      ? (asset as StockAsset | EtfAsset | FundAsset).regular_market_price ??
        (asset.type === "fund" ? (asset as FundAsset).nav_price ?? null : null)
      : null;

  // Price change is period-dependent:
  // - 1D: use market open (or previous close if open missing)
  // - other periods: use first vs last point of the selected chart series
  const stockOrEtf = (asset.type === "stock" || asset.type === "etf" || asset.type === "fund")
    ? (asset as StockAsset | EtfAsset | FundAsset)
    : null;

  const priceChange = computeAssetPriceChange(
    stockOrEtf,
    period,
    currentPrice,
    displayedChartData,
    dailyReferenceChartData
  );

  const isPositive = priceChange >= 0;

  // Get currency symbol from asset
  const assetCurrency = (asset.type === "stock" || asset.type === "etf" || asset.type === "fund" || asset.type === "crypto")
    ? (asset as StockAsset | EtfAsset | FundAsset).currency_symbol
    : undefined;

  const previousClosePrice = getValidatedPreviousClose(stockOrEtf, dailyReferenceChartData);

  const xAxisTicks = useMemo(() => {
    if (!displayedChartData?.price_points?.length) {
      return [];
    }

    return getEvenlySpacedTicks(
      displayedChartData.price_points,
      compact ? 4 : 5
    ).map((point) => point.timestamp);
  }, [compact, displayedChartData]);

  const yAxisTicks = useMemo(() => {
    if (!displayedChartData?.price_points?.length) {
      return [];
    }

    const prices = displayedChartData.price_points.map((point) => point.price);
    if (period === "1d" && previousClosePrice && previousClosePrice > 0) {
      prices.push(previousClosePrice);
    }

    return getPriceTicks(Math.min(...prices), Math.max(...prices), compact ? 4 : 5);
  }, [compact, displayedChartData, period, previousClosePrice]);

  // Calculate Y-axis domain with padding and include previous close for 1D.
  const yAxisDomain = displayedChartData?.price_points?.length
    ? (() => {
        const prices = displayedChartData.price_points.map((p) => p.price);
        if (period === "1d" && previousClosePrice && previousClosePrice > 0) {
          prices.push(previousClosePrice);
        }
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        if (yAxisTicks.length >= 2) {
          return [yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]];
        }
        const padding = (max - min) * 0.1 || max * 0.05;
        return [min - padding, max + padding];
      })()
    : ["auto", "auto"];

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <AssetLogo asset={asset} size="lg" />
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-2xl">{asset.symbol}</CardTitle>
                <AssetTypeBadge type={asset.type} />
              </div>
              <p className="text-muted-foreground">{asset.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
              {timePeriods.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                    period === p.value 
                      ? "bg-background text-foreground shadow-sm" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Exchange Selector if multiple exchanges available */}
            {otherExchanges.length > 0 && onExchangeChange && (asset.type === "stock" || asset.type === "etf") && (
               <Select 
                 value={asset.asset_id} 
                 onValueChange={onExchangeChange}
               >
                 <SelectTrigger className="w-[120px]">
                   <SelectValue placeholder="Exchange" />
                 </SelectTrigger>
                 <SelectContent>
                   {/* Include current asset */}
                   <SelectItem value={asset.asset_id}>
                     {getExchangeLabel(asset.exchange) || asset.exchange} ({getCurrencySymbol(asset.currency_symbol)})
                   </SelectItem>
                   {/* Include other variants */}
                   {otherExchanges.map((ex) => (
                     <SelectItem key={ex.asset_id} value={ex.asset_id}>
                        {"exchange" in ex
                          ? getExchangeLabel(ex.exchange) || ex.exchange
                          : "Unknown"} ({getCurrencySymbol("currency_symbol" in ex ? ex.currency_symbol : undefined)})
                     </SelectItem>
                   ))}
                 </SelectContent>
               </Select>
            )}

            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Price and Change */}
        <div className="flex items-baseline gap-4 mb-4">
          {loading ? (
            <Skeleton className="h-10 w-32" />
          ) : currentPrice !== null ? (
            <>
              <span className="text-3xl font-bold">
                {formatPrice(currentPrice, assetCurrency)}
              </span>
              <span
                className={`flex items-center gap-1 text-lg font-medium ${
                  isPositive ? "text-green-500" : "text-red-500"
                }`}
              >
                {isPositive ? (
                  <TrendingUp className="h-5 w-5" />
                ) : (
                  <TrendingDown className="h-5 w-5" />
                )}
                {isPositive ? "+" : ""}
                {priceChange.toFixed(2)}%
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">No price data</span>
          )}
        </div>

        {/* Chart */}
        <div className={compact ? "h-[420px] w-full" : "h-[400px] w-full"}>
          {loading ? (
            <Skeleton className="h-full w-full" />
          ) : error || !displayedChartData?.price_points?.length ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              {error || "No chart data available for this period"}
            </div>
          ) : (
            <ChartContainer config={chartConfig} className="h-full w-full">
              <LineChart
                accessibilityLayer
                data={displayedChartData.price_points}
                margin={compact ? { top: 12, left: 18, right: 28, bottom: 4 } : { top: 16, left: 20, right: 32, bottom: 8 }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  tick={{ fontSize: 11 }}
                  ticks={xAxisTicks}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    if (period === "1d") {
                      return date.toLocaleTimeString(undefined, {
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    }
                    if (period === "1y" || period === "5y" || period === "max") {
                      return date.toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                      });
                    }
                    return date.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    });
                  }}
                  interval={0}
                  minTickGap={compact ? 80 : 64}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  tick={{ fontSize: 11 }}
                  ticks={yAxisTicks}
                  width={compact ? 48 : 52}
                  tickFormatter={(value) => {
                    const symbol = getCurrencySymbol(assetCurrency);
                    if (value < 1) return `${symbol}${value.toFixed(4)}`;
                    if (value >= 1000) return `${symbol}${(value / 1000).toFixed(1)}k`;
                    return `${symbol}${value.toFixed(2)}`;
                  }}
                  domain={yAxisDomain as [number, number]}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      labelFormatter={(value) =>
                        new Date(value).toLocaleString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      }
                      formatter={(value) =>
                        typeof value === "number"
                          ? formatPrice(value, assetCurrency)
                          : String(value)
                      }
                    />
                  }
                />
                {period === "1d" && previousClosePrice && previousClosePrice > 0 && (
                  <ReferenceLine
                    y={previousClosePrice}
                    stroke="#94a3b8"
                    strokeWidth={1.25}
                    strokeDasharray="4 5"
                    strokeOpacity={0.75}
                    ifOverflow="extendDomain"
                    label={{
                      value: `Prev close ${formatPrice(previousClosePrice, assetCurrency)}`,
                      position: "top",
                      fill: "#94a3b8",
                      fontSize: 10,
                    }}
                  />
                )}
                <Line
                  dataKey="price"
                  type="monotone"
                  stroke={isPositive ? "hsl(142, 76%, 36%)" : "hsl(0, 84%, 60%)"}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 4,
                    fill: isPositive ? "hsl(142, 76%, 36%)" : "hsl(0, 84%, 60%)",
                  }}
                />
              </LineChart>
            </ChartContainer>
          )}
        </div>

        {/* Asset Details - hide in compact mode */}
        {!compact && (asset.type === "stock" || asset.type === "etf" || asset.type === "fund") && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
            <div>
              <p className="text-sm text-muted-foreground">{asset.type === "fund" ? "Family" : "Exchange"}</p>
              <p className="font-medium">
                {asset.type === "fund"
                  ? asset.fund_family || "N/A"
                  : getExchangeLabel(asset.exchange) || asset.exchange}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Currency</p>
              <p className="font-medium">{getCurrencyName(asset.currency_symbol)} ({getCurrencySymbol(asset.currency_symbol)})</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">ISIN</p>
              <p className="font-medium text-xs">{asset.isin || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Type</p>
              <p className="font-medium capitalize">{asset.type}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
